/**
 * Design tokens for both shells (CLAUDE.md Section 9).
 *
 * Guest shell: "Ink & Lagoon", dark editorial, Playfair Display headings.
 * Ops shell: "sunlight mode", light, high contrast, 18 px floor, 48 px targets.
 *
 * Components never name a shell. They use the semantic names below, which each
 * shell fills in with CSS variables at the root element. One component set,
 * two skins.
 */

export type Shell = 'guest' | 'ops';

/** Raw brand palette, straight from the brand kit. Do not use these directly in a component. */
export const palette = {
  ink: '#0a1310',
  ink2: '#0e1915',
  inkDeep: '#070f0c',
  bone: '#ede7da',
  lagoon: '#8fb8a8',
  lagoonDeep: '#2f6b57',
  sunGold: '#E9C46A',
  almendro: '#96683F',
  paper: '#f4f4f0',
  white: '#ffffff',
  nearBlack: '#14181a',
} as const;

/** Every semantic color a component may reference. */
export const colorNames = [
  'ground',
  'surface',
  'surfaceDeep',
  'line',
  'ink',
  'inkMuted',
  'inkFaint',
  'accent',
  'onAccent',
  'mark',
  'finePrint',
  'ok',
  'warn',
  'danger',
  'info',
] as const;

export type ColorName = (typeof colorNames)[number];

export type ShellTokens = {
  colors: Record<ColorName, string>;
  fonts: { display: string; body: string };
  /** Smallest body size the shell allows, in px. */
  bodySizePx: number;
  /**
   * The caption size, in px. It used to be derived as 82 percent of the body
   * size, which quietly put the ops shell at 15 px and broke the 18 px floor in
   * Section 9 on every status pill, badge and lede. A shell says its own number
   * now, and the test below the tokens holds ops to the floor.
   */
  captionSizePx: number;
  /** Smallest tap target the shell allows, in px. */
  tapTargetPx: number;
  radiusPx: number;
};

/**
 * Guest: Ink & Lagoon. Body text is Bone at 76 percent and captions at 60
 * percent, pre-blended over the ground so a token is a flat color and a
 * component never has to guess an opacity.
 *
 * Captions were 48 percent, which is what the brand kit says and what Lighthouse
 * failed the guest shell on (defect D8): #777971 on Ink is 4.27:1, and 4.07:1 on
 * the lifted surface, at 14 px. Neither clears the 4.5:1 floor in Section 9's
 * accessibility line. 60 percent Bone is #929289, which is 6.0:1 on Ink and
 * 5.7:1 on the lifted surface, and still reads as the quiet voice on the page:
 * the caption is a caption by size and by weight, not by being too faint to
 * read on a phone in daylight. The tokens test holds both numbers now.
 */
export const guestTokens: ShellTokens = {
  colors: {
    ground: palette.ink,
    surface: palette.ink2,
    surfaceDeep: palette.inkDeep,
    line: '#26302b',
    ink: palette.bone,
    inkMuted: '#b6b4aa',
    inkFaint: '#929289',
    accent: palette.lagoon,
    onAccent: palette.inkDeep,
    mark: palette.sunGold,
    finePrint: palette.almendro,
    ok: '#7fc49b',
    warn: palette.sunGold,
    danger: '#e08a7a',
    info: '#9fb9d0',
  },
  fonts: {
    display: "'Playfair Display', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, 'Times New Roman', serif",
    body: "'Archivo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  bodySizePx: 17,
  captionSizePx: 14,
  tapTargetPx: 44,
  radiusPx: 4,
};

/**
 * Ops: sunlight mode. Light ground, near black type, one darkened accent, and
 * status colors picked to survive jungle glare and a cracked screen. No dark
 * mode, on purpose.
 */
export const opsTokens: ShellTokens = {
  colors: {
    ground: palette.paper,
    surface: palette.white,
    surfaceDeep: '#e6e6df',
    line: '#cfcfc6',
    ink: palette.nearBlack,
    inkMuted: '#454e4c',
    inkFaint: '#66706e',
    accent: palette.lagoonDeep,
    onAccent: palette.white,
    mark: '#8a5a12',
    finePrint: '#66706e',
    ok: '#0f6b3d',
    warn: '#8a5a12',
    danger: '#a51c1c',
    info: '#14508c',
  },
  fonts: {
    display: "'Archivo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    body: "'Archivo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  bodySizePx: 18,
  // Not 82 percent of the body size. Sunlight mode has a floor, and a caption
  // in the glare on a cracked screen is the first thing to go. A caption here
  // is a caption by weight and color, never by being smaller.
  captionSizePx: 18,
  tapTargetPx: 48,
  radiusPx: 10,
};

export const shells: Record<Shell, ShellTokens> = {
  guest: guestTokens,
  ops: opsTokens,
};

/** "#0a1310" to "10 19 16", the channel triplet Tailwind needs for alpha support. */
export function hexToRgbTriplet(hex: string): string {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const n = Number.parseInt(full, 16);
   
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

export function cssVariableName(name: ColorName): string {
  return `--c-${name.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())}`;
}

/** The block a shell drops into its global stylesheet so every component resolves. */
export function shellCssVariables(shell: Shell): string {
  const t = shells[shell];
  const lines = colorNames.map((name) => `  ${cssVariableName(name)}: ${hexToRgbTriplet(t.colors[name])};`);
  lines.push(`  --font-display: ${t.fonts.display};`);
  lines.push(`  --font-body: ${t.fonts.body};`);
  lines.push(`  --body-size: ${t.bodySizePx}px;`);
  lines.push(`  --caption-size: ${t.captionSizePx}px;`);
  lines.push(`  --tap-target: ${t.tapTargetPx}px;`);
  lines.push(`  --radius: ${t.radiusPx}px;`);
  return `:root {\n${lines.join('\n')}\n}`;
}

/** Brand strings that belong beside the tokens because manifests need them. */
export const brand = {
  parent: 'Dia Bonito Resorts',
  property: 'Dia Bonito Solarte',
  legalEntity: 'Dia Bonito, S. de R.L.',
  guestAppName: 'Dia Bonito Solarte',
  guestAppShortName: 'Dia Bonito',
  opsAppName: 'Dia Bonito Ops',
  opsAppShortName: 'Ops',
} as const;
